/**
 * Centralized error handler utility for consistent error handling across all pages
 * Simply passes through backend error messages without modification
 */

/**
 * Process error and return backend message directly
 * @param {Error} error - The error object from API call
 * @param {string} entityName - Name of the entity (e.g., "Job", "Schedule", "Environment")
 * @param {string} operation - The operation being performed (e.g., "delete", "update", "create")
 * @returns {string} - Backend error message
 */
export const handleError = (error, entityName = "Record", operation = "process") => {
  // Extract error message from different error structures
  // Check for axios-style error response first, then plain Error message
  const errorMessage = error.response?.data?.message || 
                       error.response?.data?.msg || 
                       error.message || 
                       `Error ${operation}ing ${entityName.toLowerCase()}`;

  // Return backend message directly
  return errorMessage;
};

/**
 * Handle delete operation errors specifically
 * @param {Error} error - The error object from API call
 * @param {string} entityName - Name of the entity being deleted
 * @returns {string} - User-friendly error message
 */
export const handleDeleteError = (error, entityName = "Record") => {
  return handleError(error, entityName, "delete");
};

/**
 * Handle create operation errors specifically
 * @param {Error} error - The error object from API call
 * @param {string} entityName - Name of the entity being created
 * @returns {string} - User-friendly error message
 */
export const handleCreateError = (error, entityName = "Record") => {
  return handleError(error, entityName, "create");
};

/**
 * Handle update operation errors specifically
 * @param {Error} error - The error object from API call
 * @param {string} entityName - Name of the entity being updated
 * @returns {string} - User-friendly error message
 */
export const handleUpdateError = (error, entityName = "Record") => {
  return handleError(error, entityName, "update");
};

/**
 * Handle fetch/read operation errors specifically
 * @param {Error} error - The error object from API call
 * @param {string} entityName - Name of the entity being fetched
 * @returns {string} - User-friendly error message
 */
export const handleFetchError = (error, entityName = "Record") => {
  return handleError(error, entityName, "fetch");
};

/**
 * Handle toggle active flag errors specifically
 * @param {Error} error - The error object from API call
 * @param {string} entityName - Name of the entity being toggled
 * @returns {string} - User-friendly error message
 */
export const handleToggleError = (error, entityName = "Record") => {
  return handleError(error, entityName, "toggle");
};

export default {
  handleError,
  handleDeleteError,
  handleCreateError,
  handleUpdateError,
  handleFetchError,
  handleToggleError,
};
